<template>
	<div class="navbar theme bg box-shadow">
		<div class="navbar-brand oscar-logo">
			<!-- Logo -->
			<img
				:src="brand.logoHeader"
				alt="."
				class="d-none d-sm-none d-md-inline d-lg-inline mr-1 clickable"
				@click="goHome()"
			/>
			<!-- Hamburger -->
			<a
				v-if="sidenavExists"
				class="d-lg-none"
				data-toggle="modal"
				data-target="#aside"
				data-pjax-click-state="external"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="19"
					height="38"
					viewBox="66 113 380 285"
					preserveAspectRatio="none"
				>
					<path d="M80 304h352v16H80zM80 248h352v16H80zM80 192h352v16H80z" />
				</svg>
			</a>
		</div>

		<ul class="nav flex-row order-2">
			<!-- Unread Messages -->
			<li class="nav-item dropdown" v-if="unread.length > 0">
				<a class="nav-link px-3 no-select" data-toggle="dropdown">
					<i class="fa fa-bell fa-lg"></i>
					<span class="badge badge-pill up danger">{{ unread.length }}</span>
				</a>
				<div class="dropdown-menu dropdown-menu-right w-xxl animate fadeIn mt-2 p-0">
					<div class="scrollable" style="max-height: 350px">
						<div class="list">
							<div
								v-for="msg in unread"
								:key="msg.id"
								class="list-item hover-darken clickable"
								@click="followMessage(msg)"
							>
								<span class="w-32 avatar circle success align-self-center">
									<span class="fa fa-comment fa-lg"></span>
								</span>
								<div class="list-body">
									<div class="d-flex flex-row b-b">
										<div class="flex">
											<a class="item-title _500">{{ msg.assessment_name }}</a>
											<div class="text-xxs text-muted mb-1">
												<span>{{ msg.student_name }}</span>
												<span v-if="msg.delivery_name">• {{ msg.delivery_name }}</span>
											</div>
										</div>
										<div v-if="msg.message">
											<div class="item-date text-xs text-muted">
												{{ fs.shortDate(msg.message.created_at) }}
											</div>
											<div class="item-date text-xs text-muted">
												{{ fs.time(msg.message.created_at) }}
											</div>
										</div>
									</div>
									<div class="item-except text-sm text-muted h-1x" v-html="msg.message.text"></div>
									<div class="item-tag tag hide"></div>
								</div>
							</div>
						</div>
					</div>
					<div class="d-flex px-3 py-2 b-t">
						<div class="flex">
							<span
								>{{ unread.length }}
								{{ unread.length > 1 ? "Unread messages" : "Unread message" }}</span
							>
						</div>
						<a @click="viewAllUnread">
							View all
							<i class="fa fa-angle-right text-muted"></i>
						</a>
					</div>
				</div>
			</li>

			<!-- Change Tenant Dropdown/Tenant Display -->
			<li v-if="user.client && sortedClients.length == 0" class="d-flex align-items-center pr-3">
				<span class="px-2">{{ user.client.name }}</span>
			</li>
			<li v-if="user.client && sortedClients.length > 0" class="dropdown d-flex align-items-center pr-3">
				<a
					data-toggle="dropdown"
					class="d-flex align-items-center"
					data-pjax-click-state="anchor-empty"
					aria-expanded="false"
				>
					<span class="px-2">
						{{ user.client.name }}
						<i class="fa fa-caret-down"></i>
					</span>
				</a>
				<div v-if="user.clients" class="dropdown-menu dropdown-menu-right w animate fadeIn">
					<a class="dropdown-item disabled">
						<strong>Change Tenant</strong>
					</a>
					<div class="dropdown-divider"></div>
					<a
						v-for="client in sortedClients"
						:key="client.client_id"
						@click="changeClient(client.client_id)"
						class="dropdown-item"
						>{{ client.name }}</a
					>
				</div>
			</li>

			<!-- User Dropdown -->
			<li class="dropdown d-flex align-items-center">
				<a
					data-toggle="dropdown"
					class="d-flex align-items-center"
					data-pjax-click-state="anchor-empty"
					aria-expanded="false"
				>
					<span class>
						{{ user.first_name }} {{ user.last_name }}
						<i class="fa fa-caret-down"></i>
					</span>
				</a>
				<div class="dropdown-menu dropdown-menu-right w pt-0 mt-2 animate fadeIn">
					<div class="row no-gutters b-b mb-2">
						<!-- <div class="col-6 b-r">
							<a href="app.message.html" class="py-2 pt-3 d-block text-center">
								<i class="fa text-md fa-comment text-muted"></i>
								<small class="d-block">Chat</small>
							</a>
						</div>
						<div class="col-6">
							<a href="app.inbox.html" class="py-2 pt-3 d-block text-center">
								<i class="fa text-md fa-video-camera text-muted"></i>
								<small class="d-block">Video</small>
							</a>
						</div>-->
					</div>
					<template v-if="user.team_id">
						<div style="padding: .25rem 1.5rem;">
							<i class="fas fa-hands-helping fa-fw mr-1" v-tippy :title="cstTooltip" />
							{{ user.team_name }}
						</div>
						<div class="dropdown-divider"></div>
					</template>
					<a class="dropdown-item" href="#/profile">
						<span>Profile</span>
					</a>
					<!-- <a class="dropdown-item" href="#/profile?tab=2">
						<span>Settings</span>
					</a>-->
					<div class="dropdown-divider"></div>
					<a class="dropdown-item" href="https://mzscore.zendesk.com/hc/en-us/categories/204506348-OSCAR-Classroom-Help-Documents" target="_blank"
						>Need help?</a
					>
					<a class="dropdown-item" @click="logout">Sign out</a>

					<!-- <a class="dropdown-item" @click="startOverlay()">DEBUG: Auto-logout</a> -->
				</div>
			</li>
		</ul>

		<div class="relog-overlay dark" :class="{ 'show-30': overlayed }"></div>
		<div class="inner-overlay" :class="{ 'show-1': overlayed }">
			<div class="dark h-100 d-flex flex align-items-center rounded">
				<div class="mx-auto w-xl w-auto-xs animate fadeIn text-center text-match-color">
					<div class="my-3">
						<img :src="brand.logoMono" class="logo-mono" />
						<div class="mt-4 font-bold">
							{{ user.first_name }} {{ user.last_name }}
							<span v-if="loggedOut"></span>
						</div>
					</div>
					<div class="md-form-group" v-if="!loggedOut">
						<input type="text" class="md-input text-center" readonly :value="timeoutCount + 1 + 's'" />
						<label class="block w-100">You will be logged out</label>
					</div>
					<div class="md-form-group" v-if="loggedOut">
						<input
							type="password"
							class="md-input text-center"
							autocomplete="off"
							v-model="overlayPassword"
							@keydown="overlayKeydown"
						/>
						<label class="block w-100">Your Password</label>
					</div>
					<div class="my-3">
						<button class="btn theme" @click="unlockOverlay" v-if="!loggedOut">Unlock</button>
						<a class="btn theme px-2" href="#/" v-tippy title="Change User" v-if="loggedOut">
							<i class="far fa-user"></i>
						</a>
						<button
							:disabled="!overlayPassword"
							class="btn"
							:class="{ [overlayPassword ? 'theme' : 'btn-secondary']: true }"
							@click="unlockOverlay"
							v-if="loggedOut"
						>
							Sign In
						</button>
					</div>
				</div>
			</div>
		</div>

		<b-modal id="eulaModal" size="lg" v-model="eulaModal" no-close-on-backdrop no-close-on-esc>
			<template slot="modal-header">
				<h5 class="modal-title pl-4">EULA</h5>
			</template>
			<div id="eulaContainer" class="p-4 modal-scroll" v-html="EULA.text"></div>
			<template slot="modal-footer">
				<button @click="logout" class="btn btn-flat btn-danger">Cancel</button>
				<button
					@click="acceptEula"
					class="btn btn-flat"
					:disabled="!eulaScrolled"
					:class="{ [eulaScrolled ? 'btn-success' : 'btn-secondary']: true }"
				>
					Accept
				</button>
			</template>
		</b-modal>

		<b-modal id="termsModal" size="lg" v-model="termsModal" no-close-on-backdrop no-close-on-esc v-if="user.client">
			<template slot="modal-header">
				<h5 class="modal-title pl-4">Terms Of Use</h5>
				<h5 class="modal-title text-muted pr-4">{{ user.client.name }}</h5>
			</template>
			<div class="p-4" v-html="user.client.terms_text"></div>
			<template slot="modal-footer">
				<button @click="logout" class="btn btn-flat btn-danger">Cancel</button>
				<button @click="acceptTerms" class="btn btn-flat btn-success">Accept</button>
			</template>
		</b-modal>

		<b-modal id="versionModal" v-model="versionModal" no-close-on-backdrop no-close-on-esc v-if="user.client">
			<template slot="modal-header">
				<h5 class="modal-title pl-4">New Version Detected!</h5>
			</template>
			<p class="p-4">
				We've detected a new version of OSCAR. Please refresh to get the latest version ({{ serverVersion }}).
			</p>
			<template slot="modal-footer">
				<button @click="reload()" class="btn btn-flat btn-success">Refresh</button>
			</template>
		</b-modal>
	</div>
</template>

<script>
import AuthService from "@/services/AuthService";
import MessageService from "@/services/MessageService";
import TimeoutService from "@/services/TimeoutService";
import VersionService from "@/services/VersionService";
import ViewingDriver from "@/services/ScoringDrivers/ViewingDriver";
import fs from "@/services/FormatService";
import Notie from "@/services/NotieService";
import Router from "@/router";
import moment from "moment";
import BrandService from "@/services/BrandService";
var brand = BrandService.getBrand();
import pack from "../../../package.json";

import EULA from "@/services/EULA";

export default {
	name: "Navbar",

	props: ["refresh"],

	data() {
		return {
			fs: fs,
			brand: brand,
			EULA: EULA,
			user: {},
			overlayed: false,
			timeoutTimer: null,
			timeoutCount: 0,
			loggedOut: false,
			overlayPassword: "",
			sidenavExists: false,
			termsModal: false,
			eulaModal: false,
			eulaScrolled: false,
			ts: {},
			version: "",

			unread: [],

			versionChecker: null,
			unreadCheck: null,
			versionModal: false,
			serverVersion: "???",

			cstTooltip: `<div>Collaborative Scoring Team</div><div class="text-xxs text-muted mt-2">During Second Scoring, you will only be matched with student work from the same collaborative scoring team</div>`
		};
	},

	created() {
		this.ts = TimeoutService;
		this.version = pack.version;
		this.checkVersion();
		this.versionChecker = setInterval(this.checkVersion, 60 * 1000);

		this.checkUnread();
		this.unreadChecker = setInterval(this.checkUnread, 60 * 1000);

		AuthService.getUser().then(user => {
			this.user = user;
		});
	},

	mounted() {
		let sidenav = document.getElementById("aside");
		if (sidenav) {
			this.sidenavExists = true;
		}

		TimeoutService.registerCallback("navbar", () => {
			this.startOverlay();
		});

		this.advanceModals();

		window.addEventListener("resize", this.eulaScrollCheck);
	},

	destroyed() {
		clearInterval(this.versionChecker);
		clearInterval(this.unreadChecker);
		TimeoutService.clearCallback("navbar");

		window.removeEventListener("resize", this.eulaScrollCheck);
	},

	computed: {
		sortedClients() {
			let clients = _.filter(this.user.clients, uc => {
				return uc.client_id != this.user.client.id;
			});
			return _.sortBy(clients, c => {
				return c.name.toLowerCase();
			});
		}
	},

	watch: {
		refresh() {
			this.refreshUser();
		}
	},

	methods: {
		logout() {
			AuthService.logout();
		},

		reload() {
			window.location.reload(true);
		},

		refreshUser() {
			AuthService.getUser(true).then(user => {
				this.user = user;
				this.checkUnread();
			});
		},

		checkVersion() {
			if (!this.overlayed) {
				VersionService.getVersion().then(resp => {
					this.serverVersion = resp.data;
					if (this.serverVersion == "dev2") {
						return;
					}

					let clientBuild = this.buildFromVersion(this.version);
					let serverBuild = this.buildFromVersion(this.serverVersion);

					console.log("Compare version", clientBuild, serverBuild, serverBuild != clientBuild);
					if (serverBuild != clientBuild) {
						this.versionModal = true;
					}
				});
			}
		},

		buildFromVersion(version) {
			let parts = version.split(".");
			return parts[parts.length - 1];
		},

		checkUnread() {
			MessageService.getUnreadMessages(true).then(resp => {
				this.unread = resp.data;
			});
		},

		acceptEula() {
			AuthService.acceptEula()
				.then(resp => {
					this.advanceModals();
				})
				.catch(err => {
					Notie.error("Could not accept EULA!");
				});
		},

		eulaScrollHook() {
			let eula = document.getElementById("eulaContainer");
			eula.addEventListener("scroll", this.eulaScrollCheck);
			//Need to do one initial check in case the div isn't scrollable to begin with
			//But it has to be delayed I guess or else the modal won't actually be rendered
			setTimeout(this.eulaScrollCheck, 100);
		},

		eulaScrollCheck() {
			let eula = document.getElementById("eulaContainer");
			if (eula.scrollHeight - (eula.scrollTop + eula.clientHeight) < 4) {
				this.eulaScrolled = true;
			}
		},

		acceptTerms() {
			AuthService.acceptTerms()
				.then(resp => {
					this.advanceModals();
				})
				.catch(err => {
					Notie.error("Could not accept terms!");
				});
		},

		advanceModals() {
			AuthService.getUser().then(user => {
				this.user = user;
				let client = this.user.client;
				let showEULA = false;
				let showTerms = false;

				if (EULA.required) {
					let accepted_at = moment(user.eula_accepted_at);
					let updated_at = moment(EULA.updated_at);

					if (!user.eula_accepted || updated_at.isAfter(accepted_at)) {
						this.eulaScrollHook();
						showEULA = true;
					}
				}

				if (client.terms_required) {
					let accepted_at = moment(user.terms_accepted_at);
					let updated_at = moment(client.terms_updated_at);

					if (!user.terms_accepted || updated_at.isAfter(accepted_at)) {
						showTerms = true;
					}
				}

				if (showEULA) {
					this.eulaModal = true;
					this.termsModal = false;
				} else if (showTerms) {
					this.eulaModal = false;
					this.termsModal = true;
				} else {
					this.eulaModal = false;
					this.termsModal = false;
				}
			});
		},

		startOverlay() {
			this.versionModal = false;
			this.overlayed = true;
			this.overlayPassword = "";
			let timeout = Math.floor(TimeoutService.getServerTimeout() / 1000);
			this.$set(this, "timeoutCount", timeout);
			clearInterval(this.timeoutTimer);
			this.timeoutTimer = setInterval(() => {
				if (this.timeoutCount == 0) {
					this.loggedOut = true;
					clearInterval(this.timeoutTimer);
				} else {
					this.$set(this, "timeoutCount", this.timeoutCount - 1);
				}
			}, 1000);
		},

		getKey(e) {
			//Presumably necessary to polyfill for Safari
			if (e.key) return e.key;

			let keyFromCode = String.fromCharCode(e.keyCode);
			if (keyFromCode) return keyFromCode;

			if (e.keyCode === 13) return "Enter";
			if (e.keyCode === 16) return "Shift";
			if (e.keyCode === 8) return "Tab";
			return null;
		},

		overlayKeydown(event) {
			if (event.key == "Enter") {
				this.unlockOverlay();
			}
		},

		unlockOverlay() {
			if (this.loggedOut) {
				AuthService.login(this.user.email, this.overlayPassword)
					.then(resp => {
						AuthService.getUser()
							.then(resp => {
								this.$emit("login", true);
								this.doUnlockOverlay();
							})
							.catch(err => {
								console.log(err);
								Notie.error("Failed to initialize user", err);
							});
					})
					.catch(err => {
						console.log(err);
						Notie.error("Failed to log in", err);
					});
			} else {
				//Just refresh user to ping to the server
				AuthService.getUser(true)
					.then(user => {
						this.user = user;
						this.doUnlockOverlay();
					})
					.catch(err => {
						console.log(err);
						Notie.error("Failed to initialize user", err);
					});
			}
		},

		doUnlockOverlay() {
			clearInterval(this.timeoutTimer);
			this.overlayed = false;
			this.loggedOut = false;
		},

		changeClient(clientID) {
			AuthService.changeClient(clientID).then(() => {
				AuthService.getUser(true).then(user => {
					this.user = user;
					Router.push("/landing?client=" + clientID);
				});
			});
		},

		goHome() {
			this.$router.push("/landing");
		},

		followMessage(msg) {
			let aid = msg.message.assessment_id;
			let iid = msg.message.item_id;
			let rid = msg.message.response_id;
			ViewingDriver.enter(this, aid, iid, rid);
		},

		viewAllUnread() {
			this.$router.push("/view_unread");
		}
	}
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.dropdown-item:hover.disabled {
	background: transparent;
	cursor: default;
}
.relog-overlay {
	position: fixed;
	z-index: 2000;
	top: 0;
	bottom: 0;
	left: 0;
	right: 0;

	opacity: 0;
	pointer-events: none;
}
.inner-overlay {
	position: fixed;
	z-index: 2001;
	left: 50%;
	top: 50%;
	transform: translate(-50%, -50%);
	width: 50%;
	height: 50%;

	min-height: 320px;
	min-width: 320px;

	opacity: 0;
	pointer-events: none;
}
.show-1 {
	opacity: 1;
	transition: opacity 1s;
	pointer-events: all;
}
.show-30 {
	opacity: 1;
	pointer-events: all;
	transition: opacity 20s;
}
.text-match-color {
	color: rgb(255, 255, 255, 0.45);
}
.white-bg {
	background-color: rgba(255, 255, 255, 0.7);
}
.clickable {
	cursor: pointer;
}
.badge.up {
	padding: 0.15rem 0.3rem;
	position: relative;
	top: -0.85rem;
	left: -0.75rem;
	margin-right: -1rem;
	font-size: 90%;
	border: 1px solid rgba(255, 255, 255, 0.75);
	background-clip: padding-box;
	min-width: 1rem;
}
</style>



// WEBPACK FOOTER //
// src/components/global/Navbar.vue